import { Box, List, ListItem, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import {
  AppBarCategoryItem,
  AppBarCategorySection,
  AppBarMenuListSections,
} from "../../../data/app-side-menu-list-items";
import { Icons } from "../../../models/assets-files";
import { Styles } from "../../../models/react.models";
import CollapseAccordion from "../../common/collapse-accordion/CollapseAccordion";
import IconText from "../../common/IconText";
import Image from "../../common/Image";
import { ImageService } from "../../../services/image.service";
import { StoreState } from "../../../store/store";
import { useSelector } from "react-redux";

interface AppBarMenuListProps {
  selectedItemPath: string;
  handleItemClick: (categoryItem: AppBarCategoryItem) => void;
}
const AppBarMenuList = ({
  selectedItemPath,
  handleItemClick,
}: AppBarMenuListProps) => {
  const [expandedId, setExpandedId] = useState<string>("");
  const [itemsToShow, setItemsToShow] = useState<AppBarCategorySection[]>(
    AppBarMenuListSections
  );
  const user = useSelector((state: StoreState) => state.user.loggedInUser);

  const initExpandedId = () => {
    const item = AppBarMenuListSections.flatMap(
      (section) => section.categoryItems
    ).find((item) =>
      item.subCategories?.some(
        (subCategory) => subCategory.path === selectedItemPath
      )
    );

    if (item) {
      setExpandedId(item.id);
    }
  };

  const handleExpandClick = (panel: string) => {
    setExpandedId(expandedId === panel ? "" : panel);
  };

  const isExpandedItem = (item: AppBarCategoryItem) => {
    return !!item.subCategories;
  };

  const isSelectedItem = (categoryItem: AppBarCategoryItem) => {
    let isParentOfSelected = false;
    if (categoryItem.subCategories) {
      isParentOfSelected = categoryItem.subCategories.some(
        (category) => category.path === selectedItemPath
      );
    }
    return categoryItem.path === selectedItemPath || isParentOfSelected;
  };

  const initItemsToShow = () => {
    const filteredItems = AppBarMenuListSections.map((section) => {
      // For admins and agents, only show "User" and "Administration" sections
      if (user?.role === "admin" || user?.role === "agent") {
        if (section.title !== "User" && section.title !== "Administration") {
          return {
            ...section,
            categoryItems: []
          };
        }
      }

      return {
        ...section,
        categoryItems: section.categoryItems.filter(
          (item) => {
            // Show products-and-services only for clients
            if (item.id === "products-and-services" && !user?.isClient) {
              return false;
            }
            // Show admin sections only for admin users
            if (item.id === "admin-clients" && user?.role !== "admin" && user?.role !== "agent") {
              return false;
            }
            // Show admin management only for admin users
            if (item.id === "admin-management" && user?.role !== "admin") {
              return false;
            }
            return true;
          }
        ),
      };
    }).filter((section) => section.categoryItems.length > 0);

    setItemsToShow(filteredItems);
  };

  useEffect(() => {
    initExpandedId();
    initItemsToShow();
  }, [selectedItemPath]);

  const styles: Styles = {
    collapseHeaderSx: {
      userSelect: "none",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "#0250A5",
      },
      transition: "all 0.3s",
      marginBlock: "3px",
      borderEndEndRadius: "50px",
      borderStartEndRadius: "50px",
      paddingInlineStart: "10px",
      height: "42px",
      width: "100%",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      color: "white",
    },
    subCategoryContainer: {
      paddingInlineEnd: "10px",
      borderEndEndRadius: "40px",
      borderStartEndRadius: "40px",
      transition: "all 0.3s",
      height: "41px",
      cursor: "pointer",
      justifyContent: "space-between",
      "&:hover": {
        backgroundColor: "#0250A5",
      },
    },
  };

  const onItemClick = (categoryItem: AppBarCategoryItem) => {
    if (categoryItem.subCategories) return;
    handleItemClick(categoryItem);
  };

  return (
    <List>
      {itemsToShow.map((section) => (
        <ListItem
          key={section.title}
          sx={{
            flexDirection: "column",
            alignItems: "flex-start",
          }}
          disablePadding
        >
          <Typography color="white" fontSize={"14px"} variant="h1">
            {section.title}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              marginBlock: "10px",
            }}
          >
            {section.categoryItems.map((categoryItem) => (
              <CollapseAccordion
                key={categoryItem.id}
                id={categoryItem.id}
                expandedId={expandedId}
                disableExpand={!isExpandedItem(categoryItem)}
                handleCollapse={(id: string) => setExpandedId(id)}
              >
                <slot name="header">
                  <Box
                    sx={{
                      ...styles.collapseHeaderSx,
                      background: isSelectedItem(categoryItem)
                        ? "#0250A5"
                        : "transparent",
                    }}
                    onClick={() =>
                      isExpandedItem(categoryItem) &&
                      handleExpandClick(categoryItem.id)
                    }
                  >
                    <IconText
                      onClick={() => onItemClick(categoryItem)}
                      iconSx={{ width: "24px" }}
                      containerSx={{
                        userSelect: "none",
                        justifyContent: "space-between",
                        width: "100%",
                        paddingInlineEnd: "10px",
                      }}
                      iconSrc={ImageService.getLocalIconPath(
                        Icons.Folders.APP_BAR_MENU,
                        categoryItem.icon
                      )}
                      textSx={{ fontWeight: "200" }}
                      text={categoryItem.title}
                    >
                      {isExpandedItem(categoryItem) && (
                        <slot name="after">
                          <Image
                            src={ImageService.getLocalIconPath(
                              Icons.Folders.GENERAL,
                              expandedId === categoryItem.id
                                ? Icons.General.WhiteArrowUp
                                : Icons.General.WhiteArrowDown
                            )}
                          />
                        </slot>
                      )}
                    </IconText>
                  </Box>
                </slot>
                {isExpandedItem(categoryItem) && (
                  <slot name="content">
                    {categoryItem.subCategories?.map((subCategory) => (
                      <IconText
                        onClick={() => onItemClick(subCategory)}
                        key={subCategory.id}
                        containerSx={{
                          marginBlock: "5px",
                          ...styles.subCategoryContainer,
                          background: isSelectedItem(subCategory)
                            ? "#0250A5"
                            : "transparent",
                        }}
                        textSx={{ color: "white", fontSize: "14px" }}
                        innerContainerSx={{
                          paddingInlineStart: "7px",
                          userSelect: "none",
                        }}
                        text={subCategory.title}
                        iconSrc={ImageService.getLocalIconPath(
                          Icons.Folders.APP_BAR_MENU,
                          subCategory.icon
                        )}
                      >
                        <slot name="after">
                          <Image
                            src={ImageService.getLocalIconPath(
                              Icons.Folders.GENERAL,
                              Icons.General.WhiteArrowRight
                            )}
                          />
                        </slot>
                      </IconText>
                    ))}
                  </slot>
                )}
              </CollapseAccordion>
            ))}
          </Box>
        </ListItem>
      ))}
    </List>
  );
};

export default AppBarMenuList;
